import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import moment from 'moment';
import { FilterCommunicationService } from '../../../../../../../core/services/filter-communication.service';
import { SubscriptionPaymentService } from '../../../../../../../core/services/subscription-payment.service';
import { CONFIG_BASE_DATE_RANGEPICKER } from '../../../../../../../core/constants/constants';
import { RepeatedVoucherByBankDateRange } from '../../../../../../../core/interfaces/subscription-payment.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { DynamicModalService } from '../../../../../../../core/services/dynamic-modal.service';
import { extractErrorMessage } from '../../../../../../../core/utils/extract-error-message.util';

@Component({
  selector: 'app-repeated-bank-vouchers-modal',
  templateUrl: './repeated-bank-vouchers-modal.component.html',
  styleUrl: './repeated-bank-vouchers-modal.component.scss',
})
export class RepeatedBankVouchersModalComponent {
  public modalHost?: {
    minimize: () => void;
    restore: () => void;
  };
  public readonly bsConfig = CONFIG_BASE_DATE_RANGEPICKER.bsConfig;
  public readonly initialRange: Date[] = CONFIG_BASE_DATE_RANGEPICKER.initialRange;

  public isLoading = false;
  public hasSearched = false;
  public errorMessage = '';
  public rows: RepeatedVoucherByBankDateRange[] = [];
  public page = 1;
  public pageSize = 10;
  public readonly pageSizeOptions = [10, 20, 50, 100];

  public form = this._fb.group({
    dateRange: [this.initialRange, Validators.required],
  });

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _subscriptionPaymentService: SubscriptionPaymentService,
    private readonly _filterCommunicationService: FilterCommunicationService,
    private readonly _dynamicModalService: DynamicModalService
  ) {}

  get totalResults(): number {
    return this.rows.length;
  }

  get totalRiskAmount(): number {
    return this.rows
      .filter((item) => Number(item.monto) > 0)
      .reduce((acc, item) => acc + Number(item.monto), 0);
  }

  get criticalRows(): RepeatedVoucherByBankDateRange[] {
    return this.rows.filter((item) => Number(item.total) >= 3);
  }

  get paginatedRows(): RepeatedVoucherByBankDateRange[] {
    const start = (this.page - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.rows.slice(start, end);
  }

  search(): void {
    const dateRange = this.form.get('dateRange')?.value as Date[] | null;

    if (!dateRange || dateRange.length < 2 || !dateRange[0] || !dateRange[1]) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.hasSearched = true;
    this.errorMessage = '';

    const startDate = moment(dateRange[0]).startOf('day').toDate();
    const endDate = moment(dateRange[1]).endOf('day').toDate();

    this._subscriptionPaymentService
      .getRepeatedVouchersByBankDateRange({
        startDate,
        endDate,
      })
      .subscribe({
        next: (response) => {
          this.rows = response.data;
          this.page = 1;
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage = extractErrorMessage(
            error,
            'es',
            'No se pudo obtener la información de comprobantes repetidos.',
          );
          this.rows = [];
          this.isLoading = false;
        },
      });
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.page = 1;
  }

  getSeverityClass(row: RepeatedVoucherByBankDateRange): 'high' | 'med' {
    return Number(row.total) >= 3 ? 'high' : 'med';
  }

  getSeverityLabel(row: RepeatedVoucherByBankDateRange): string {
    return Number(row.total) >= 3 ? 'Alta' : 'Media';
  }

  exportCsv(): void {
    const headers = ['Comprobante', 'Monto', 'Severidad', 'Repetidos'];
    const lines = this.rows.map((row) =>
      [
        `"${row.n_comprobante || 'Sin comprobante'}"`,
        Number(row.monto).toFixed(2),
        this.getSeverityLabel(row),
        `x${row.total}`,
      ].join(',')
    );

    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'comprobantes-repetidos.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  applyRowFilter(row: RepeatedVoucherByBankDateRange): void {
    const modalData = this._dynamicModalService.getModalData();
    const currentFilter = modalData?.currentFilter || {};
    const dateRange = this.form.get('dateRange')?.value as Date[] | null;

    const dateStart =
      dateRange && dateRange[0]
        ? moment(dateRange[0]).format('YYYY-MM-DD 00:00:00')
        : null;
    const dateEnd =
      dateRange && dateRange[1]
        ? moment(dateRange[1]).format('YYYY-MM-DD 23:59:59')
        : null;

    this._filterCommunicationService.changeFilter({
      ...currentFilter,
      receipt: row.n_comprobante?.trim() || '',
      amount: row.monto,
      dateRange,
      datestart: dateStart,
      dateend: dateEnd,
      bank_date_range: null,
      bank_date_start: null,
      bank_date_end: null,
    });

    this.modalHost?.minimize();
  }
}
